<script lang="ts" setup>
import { computed, inject } from 'vue'
import { NOVELMELLO_READER_DEFAULT_BACKGROUND } from '../configs/novelmello'
import { useGotoBack } from '@/composables'

defineProps<{
  bookName?: string
  chapterName?: string
  isInShelf?: boolean
  shelfLoading?: boolean
}>()

const emit = defineEmits<{
  (e: 'click-book'): void
  (e: 'toggle-shelf'): void
}>()

const gotoBack = useGotoBack()
const theme = inject<any>('theme', { background: NOVELMELLO_READER_DEFAULT_BACKGROUND })

/** 顶栏与正文同底(设计:#FFFCFA 通铺),皮肤切换时跟随 */
const barBg = computed(() => theme?.value?.background ?? theme?.background ?? NOVELMELLO_READER_DEFAULT_BACKGROUND)
</script>

<template>
  <!--
    设计稿右侧的 list(目录)图标在本实现中不渲染:
    ChapterBreadcrumb 现有契约只有 click-book / toggle-shelf 两个 emit,顶栏没有现成的目录打开途径,
    按最终裁决(99-final-check 第七节)不新增 open-toc emit,故移除该图标;
    目录入口仍由底部设置面板的 Catalogue 胶囊(已有 open-toc)承担。
  -->
  <div class="nm-reader-topbar" :style="{ background: barBg }">
    <!-- 返回 -->
    <div class="nm-topbar-side">
      <div class="nm-topbar-btn" @click="gotoBack()">
        <i-lucide-chevron-left class="nm-icon-24 nm-topbar-icon" />
      </div>
    </div>

    <!-- 书名 / 章节名:书名可点击进书首页(复用原 click-book 逻辑),章节名纯文本 -->
    <div class="nm-topbar-title">
      <span
        v-if="bookName"
        class="nm-topbar-book"
        @click="emit('click-book')"
      >{{ bookName }}</span>
      <span v-if="bookName && chapterName" class="nm-topbar-sep">/</span>
      <span v-if="chapterName" class="nm-topbar-chapter">{{ chapterName }}</span>
    </div>

    <div class="nm-topbar-side nm-topbar-side--end">
      <!-- 加入书架(保留原逻辑) -->
      <div
        class="nm-topbar-btn"
        :class="{ 'is-loading': shelfLoading }"
        @click="emit('toggle-shelf')"
      >
        <i-lucide-bookmark-check v-if="isInShelf" class="nm-icon-20 nm-topbar-icon nm-topbar-icon--accent" />
        <i-lucide-bookmark v-else class="nm-icon-20 nm-topbar-icon" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.nm-reader-topbar {
  display: flex;
  align-items: center;
  width: 100%;
  height: 48px;
  padding: 0 12px;
  box-sizing: border-box;
}

/* 左右两侧等宽,保证中间章节名视觉居中 */
.nm-topbar-side {
  flex-shrink: 0;
  min-width: 32px;
  display: flex;
  align-items: center;
}

.nm-topbar-side--end {
  justify-content: flex-end;
}

.nm-topbar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  cursor: pointer;

  &.is-loading {
    opacity: 0.5;
    pointer-events: none;
  }

  & + & {
    margin-left: 10px;
  }
}

.nm-topbar-title {
  flex: 1;
  min-width: 0;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 16px;
  color: #AB9BB8;
}

/* 书名:略深一档提示可点击,过长时最多占一半宽度 */
.nm-topbar-book {
  flex-shrink: 1;
  min-width: 0;
  max-width: 50%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #71607E;
  cursor: pointer;
}

.nm-topbar-sep {
  flex-shrink: 0;
  margin: 0 4px;
}

.nm-topbar-chapter {
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nm-icon-24 {
  width: 24px;
  height: 24px;
}

.nm-icon-20 {
  width: 20px;
  height: 20px;
}

.nm-topbar-icon,
.nm-topbar-icon :deep(*) {
  color: var(--chapter-nav-secondary, #71607E);
}

.nm-topbar-icon--accent,
.nm-topbar-icon--accent :deep(*) {
  color: #F0509B;
}
</style>
